import { useMemo } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { BellIcon } from '@phosphor-icons/react';
import { useUpcomingEvents } from '@/lib/hooks/useEvents';
import { useEventFileCounts } from '@/lib/hooks/useEventFiles';
import { useUserRsvpsForEvents } from '@/lib/hooks/useRsvps';
import { usePosts } from '@/lib/hooks/usePosts';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { useNotificationInboxState } from '@/lib/hooks/useUserNotifications';
import { useImagePreload } from '@/lib/hooks/useImagePreload';
import { HomeGreeting } from '@/components/home/HomeGreeting';
import { HomeGreetingSkeleton } from '@/components/home/HomeGreetingSkeleton';
import { HomeQuickActions } from '@/components/home/HomeQuickActions';
import { HomeQuickActionsSkeleton } from '@/components/home/HomeQuickActionsSkeleton';
import { InstallCard } from '@/components/home/InstallCard';
import { EnableNotificationsCard } from '@/components/home/EnableNotificationsCard';
import { UpcomingEventsCarousel } from '@/components/home/UpcomingEventsCarousel';
import { UpcomingEventsCarouselSkeleton } from '@/components/home/UpcomingEventsCarouselSkeleton';
import { Post } from '@/components/community/Post';
import { PostListSkeleton } from '@/components/community/PostListSkeleton';
import { getFirstName, formatDateTimeForList } from '@/lib/utils/date.utils';
import {
  getVisibleModerationStatusForViewer,
  getVisiblePostsForViewer,
} from '@/lib/utils/posts.utils';
import type { RsvpStatus } from '@/lib/types/events.types';

export function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, isLoading: profileLoading } = useUserProfile();
  const { data: upcomingEvents, isLoading: eventsLoading } =
    useUpcomingEvents();
  const { data: posts, isLoading: postsLoading } = usePosts();
  const { data: inboxState } = useNotificationInboxState();

  // Preload gradient banner image
  const gradientLoaded = useImagePreload('/assets/images/gradient-banner.svg');

  // Critical data: profile AND gradient image must be loaded
  const criticalDataLoading = profileLoading || !gradientLoaded;

  // Extract event IDs for batch RSVP fetching
  const eventIds = useMemo(
    () => upcomingEvents?.map((e) => e.id) || [],
    [upcomingEvents]
  );

  // Fetch all RSVPs for this user in a single query
  const { data: rsvpMap } = useUserRsvpsForEvents(eventIds, user?.id || null);
  const { data: fileCounts } = useEventFileCounts(eventIds);

  // Helper to get RSVP status for an event
  const getRsvpStatus = (eventId: number): RsvpStatus => {
    const rsvp = rsvpMap?.get(eventId);
    return (rsvp?.status as RsvpStatus) || null;
  };

  // Get preview events (first 3 upcoming)
  const previewEvents = upcomingEvents?.slice(0, 3) || [];

  // Extract first name from user profile
  const firstName = getFirstName(profile?.full_name || null);

  // Determine if user can register guests (Admin or Fellow only)
  const canRegisterGuest =
    profile?.role === 'admin' || profile?.role === 'fellow';
  const userIsAdmin = profile?.role === 'admin';
  const visiblePosts = getVisiblePostsForViewer({
    posts,
    userId: user?.id,
    isAdmin: userIsAdmin,
  });
  const previewPosts = visiblePosts.slice(0, 3);

  // Navigation handlers
  const handleViewEvents = () => {
    navigate({ to: '/events', search: { tab: 'going' } });
  };

  const handleRegisterGuest = () => {
    navigate({ to: '/register-guest' });
  };

  // Show full skeleton while critical data loads
  if (criticalDataLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 pb-4 mt-[18px]">
        <HomeGreetingSkeleton />
        <HomeQuickActionsSkeleton />
        <UpcomingEventsCarouselSkeleton />
        <PostListSkeleton />
      </div>
    );
  }

  return (
    <>
      <section className="w-full max-w-4xl mx-auto px-4 pb-0">
        <HomeGreeting
          firstName={firstName}
          action={
            <Link
              to="/notifications"
              aria-label="Open notifications"
              className="relative inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-500/70 bg-slate-700/80 text-slate-50 no-underline transition-colors hover:border-slate-400 hover:bg-slate-700"
            >
              <BellIcon size={22} weight="regular" />
              {inboxState?.hasNewNotifications && (
                <>
                  <span className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-red-400 ring-2 ring-slate-700" />
                  <span className="sr-only">New notifications available</span>
                </>
              )}
            </Link>
          }
        />
        <HomeQuickActions
          canRegisterGuest={canRegisterGuest}
          onViewEvents={handleViewEvents}
          onRegisterGuest={handleRegisterGuest}
        />

        <InstallCard />
        <EnableNotificationsCard />

        {eventsLoading ? (
          <UpcomingEventsCarouselSkeleton />
        ) : (
          <UpcomingEventsCarousel
            events={previewEvents}
            getRsvpStatus={getRsvpStatus}
            fileCounts={fileCounts}
          />
        )}
      </section>

      <section className="w-full max-w-4xl mx-auto px-4 mt-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="font-size font-semibold text-primary mb-3">
            Community
          </h2>
          {visiblePosts.length > 0 && (
            <Link
              to="/feed"
              className="text-sky-400 text-small hover:underline"
            >
              View all
            </Link>
          )}
        </div>

        {postsLoading ? (
          <PostListSkeleton />
        ) : previewPosts.length === 0 ? (
          <p className="text-body text-slate-400">
            No posts yet. Be the first to share something!
          </p>
        ) : (
          <div className="divide-y divide-slate-600">
            {previewPosts.map((post) => {
              const authorName = post.author_name || 'Unknown User';
              const formattedTimestamp = formatDateTimeForList(post.created_at);

              return (
                <Post
                  key={post.id}
                  postId={post.id}
                  author={authorName}
                  postTimestamp={formattedTimestamp}
                  imgUrl={post.image_url || undefined}
                  content={post.content}
                  commentCount={post.comment_count}
                  isAdminOrOwner={false}
                  moderationStatus={getVisibleModerationStatusForViewer(
                    post,
                    user?.id,
                    userIsAdmin
                  )}
                />
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
